import API from "../../services/api";
import { formatAppDateTime } from "../../utils/dateFormat";

function collectErrors(entries) {
  return entries.filter(([, result]) => result.status !== "fulfilled").map(([label]) => label);
}

function getValue(result, fallback) {
  return result.status === "fulfilled" ? result.value.data : fallback;
}

export async function loadAdminOverviewData() {
  const results = await Promise.allSettled([
    API.get("/admin/stats"),
    API.get("/admin/system-health"),
    API.get("/admin/account-set-requests"),
    API.get("/admin/notifications", { params: { limit: 6 } }),
  ]);

  const [statsRes, healthRes, requestsRes, notificationsRes] = results;
  const notificationPayload = getValue(notificationsRes, {}) || {};

  return {
    stats: getValue(statsRes, null),
    systemHealth: getValue(healthRes, null),
    requests: getValue(requestsRes, []) || [],
    notifications: notificationPayload.items || [],
    notificationsUnread: notificationPayload.unreadCount || 0,
    errors: collectErrors([
      ["stats", statsRes],
      ["system health", healthRes],
      ["requests", requestsRes],
      ["notifications", notificationsRes],
    ]),
  };
}

export async function loadAdminUsersData() {
  const result = await Promise.allSettled([API.get("/admin/users")]);
  const [usersRes] = result;

  return {
    users: getValue(usersRes, []) || [],
    errors: collectErrors([["users", usersRes]]),
  };
}

export async function loadAdminAccountsData() {
  const results = await Promise.allSettled([
    API.get("/admin/account-set-requests"),
    API.get("/admin/account-sets", { params: { includeDeleted: "false" } }),
    API.get("/admin/account-sets/deleted"),
  ]);

  const [requestsRes, activeSetsRes, deletedSetsRes] = results;

  return {
    requests: getValue(requestsRes, []) || [],
    accountSets: getValue(activeSetsRes, []) || [],
    deletedAccountSets: getValue(deletedSetsRes, []) || [],
    errors: collectErrors([
      ["requests", requestsRes],
      ["active account sets", activeSetsRes],
      ["deleted account sets", deletedSetsRes],
    ]),
  };
}

export async function loadAdminBroadcastData() {
  const results = await Promise.allSettled([API.get("/admin/database-status")]);
  const [databaseRes] = results;

  return {
    databaseStatus: getValue(databaseRes, null),
    errors: collectErrors([["database status", databaseRes]]),
  };
}

export function getAccountSetRequestTypeMeta(type) {
  switch (
    String(type || "")
      .trim()
      .toLowerCase()
  ) {
    case "link":
      return {
        value: "link",
        label: "Link Review",
        shortLabel: "Link",
        overviewLabel: "Link account set",
        badgeClass: "admin-badge-info",
        canPreview: true,
      };
    case "delete":
      return {
        value: "delete",
        label: "Delete Account Set",
        shortLabel: "Delete",
        overviewLabel: "Delete account set",
        badgeClass: "admin-badge-danger",
        canPreview: false,
      };
    case "modify":
    default:
      return {
        value: "modify",
        label: "Modify Account Set",
        shortLabel: "Modify",
        overviewLabel: "Modify account set",
        badgeClass: "admin-badge-info",
        canPreview: true,
      };
  }
}

export function formatTimestamp(timestamp) {
  if (!timestamp) return "Never";
  return formatAppDateTime(timestamp, "Never");
}

export function formatTimeAgo(ageMs) {
  if (!ageMs) return "Never";

  const seconds = Math.floor(ageMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return `${seconds}s ago`;
}

export function formatUptime(seconds) {
  if (!seconds && seconds !== 0) return "n/a";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function buildEaConfigText(cfg) {
  if (!cfg || !cfg.accountSet) return "";
  const lines = [];
  const brokersSorted = Array.isArray(cfg.brokers)
    ? [...cfg.brokers].sort((a, b) => (a.position || 0) - (b.position || 0))
    : [];

  lines.push("Premix Collector EA Config");
  lines.push(`User: ${cfg.user?.email || "Unknown"}`);
  lines.push(`Account Set: ${cfg.accountSet.name || "Unknown"} (${cfg.accountSet.id})`);
  lines.push(`Symbols Locked: ${cfg.accountSet.symbolsLocked ? "yes" : "no"}`);
  if (cfg.accountSet.futureSymbol || cfg.accountSet.spotSymbol) {
    lines.push(
      `Symbols: ${cfg.accountSet.futureSymbol || "-"} vs ${cfg.accountSet.spotSymbol || "-"}`,
    );
  }
  if (cfg.accountSet.requestedFutureSymbol || cfg.accountSet.requestedSpotSymbol) {
    lines.push(`Requested Future Symbol: ${cfg.accountSet.requestedFutureSymbol || "-"}`);
    lines.push(`Requested Spot Symbol: ${cfg.accountSet.requestedSpotSymbol || "-"}`);
  }
  lines.push("");
  lines.push("Recommended EA inputs:");
  lines.push("AutoRegister=true");
  lines.push("MagicNumberFilter=0");
  lines.push("CommentPrefix=<generated per broker tag>");
  lines.push("");

  if (cfg.duplicateLoginDetected) {
    lines.push("WARNING: Duplicate broker login detected.");
    lines.push("Generated files keep their persistent CommentPrefix tags.");
    lines.push(
      "Affected brokers disable AutoRegister and rely on manual AccountSetId/BrokerId/BrokerPosition.",
    );
    lines.push("");
  }

  brokersSorted.forEach((broker) => {
    lines.push(`--- Broker #${broker.position} (${broker.terminal}) ---`);
    lines.push(`BrokerName=${broker.brokerName || ""}`);
    lines.push(`Server=${broker.server || ""}`);
    lines.push(`AccountNumber=${broker.accountNumber || ""}`);
    lines.push(`Password=${broker.password || ""}`);
    lines.push(`AccountSetId=${cfg.accountSet.id}`);
    lines.push(`BrokerId=${broker.id}`);
    lines.push(`BrokerPosition=${broker.position}`);
    lines.push(`BrokerKey=${broker.brokerKey || ""}`);
    if (broker.collectorTag) {
      lines.push(`CommentPrefix=${broker.collectorTag}`);
    }
    if (broker.requestedSymbol) {
      lines.push(`RequestedSymbol=${broker.requestedSymbol}`);
    }
    if (broker.duplicateLogin) {
      lines.push(`DuplicateLogin=true (count=${broker.duplicateCount || 2})`);
    }
    lines.push("");
  });

  return lines.join("\n").trim();
}
